/*
 * Arrow-key caret traversal across the island boundary (design §2.a).
 *
 * A pending suggestion's card is a `Decoration.widget`, and a widget holds NO
 * document position of its own: every host position from the anchor text down
 * through the card resolves to the proposal's `to`. So the caret cannot simply
 * "walk into" the card the way it walks into text, and there is no host
 * position that means "just after the card" either. Both halves of traversal
 * have to be resolved explicitly, and they have to be exact inverses or the
 * caret gets trapped: if the position an exit lands on is also a position an
 * entry fires from in the same direction, the clinician can never get past the
 * card.
 *
 * The rule this file implements, for a card anchored at `to`:
 *
 *   ... anchor text | to |  [ card ]  | afterPos | following text ...
 *      ArrowRight at `to`               -> enter the island at its start
 *      ArrowLeft at `afterPos`          -> enter the island at its end
 *      ArrowDown from the card's line   -> enter the island at its start
 *      ArrowUp from the line after it   -> enter the island at its end
 *      island start, ArrowLeft/Up       -> exit to `to`
 *      island end, ArrowRight/Down      -> exit to `afterPos`
 *
 * HORIZONTAL AND VERTICAL ASK DIFFERENT QUESTIONS. The widget is a `block` box
 * (see AI_PROPOSAL_WIDGET_CLASS), so it always occupies its own visual line.
 * Left/Right walk one position at a time, so the caret always lands exactly on
 * the boundary before the press that should enter — position equality is the
 * whole rule. Up/Down do not: the press comes from wherever the clinician's
 * caret happens to sit along the adjacent line, so equality would only ever
 * fire from one column of it (the end of the anchor line, the start of the line
 * below) and every other column would skip past the card entirely.
 *
 * The vertical rule is therefore "is the caret on the line the card sits
 * directly below/above", which is half document structure (the boundary is in
 * the caret's own textblock, on the far side of the caret) and half layout
 * (which visual line each position paints on). Only the structural half is
 * answerable from an EditorState, so the caller injects the layout half as a
 * `SameVisualLine` probe. Note the card lives INSIDE its anchor's textblock as
 * a block child, so `EditorView.endOfTextblock('down')` — the natural-looking
 * gate, and the one the island's own exit keymap uses — cannot serve here: the
 * card's own box is a box below the caret in the same textblock, so it answers
 * false from every column of the anchor line, including the one that works
 * today.
 */
import type { Node as PMNode } from '@tiptap/pm/model'
import { type EditorState, Selection } from '@tiptap/pm/state'
import {
  type AIProposal,
  widgetProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'

// Which side of its card an island exit puts the host caret on.
export type IslandExitSide = 'before' | 'after'

export interface IslandEntryTarget {
  id: string
  // Which end of the ISLAND document the caret enters at.
  edge: 'start' | 'end'
}

/*
 * The host position an exit on `side` lands on, or null when that side has no
 * position distinct from the card's own anchor.
 *
 * `before` is the anchor itself — the caret renders at the end of the anchor
 * text, above the card. `after` has to step a position forward, because the
 * position immediately following the card IS `to` and would read as before it:
 * from a card at the end of its block that step lands at the start of the next
 * block, and mid-block it lands one character on. Null (only reachable when the
 * card ends the document and no trailing paragraph follows) means "no exit that
 * way" rather than a landing spot that re-enters this same island.
 */
export function islandExitPos(
  doc: PMNode,
  to: number,
  side: IslandExitSide
): number | null {
  if (side === 'before') {
    return to <= doc.content.size ? to : null
  }
  if (to + 1 > doc.content.size) {
    return null
  }
  const pos = Selection.near(doc.resolve(to + 1), 1).from
  return pos > to ? pos : null
}

export type IslandTraversalDirection = 'forward' | 'backward'

/*
 * Whether the caret and a card boundary paint on the same visual line — the one
 * fact vertical entry needs and an EditorState cannot hold. The host supplies
 * it from its EditorView (see IslandTraversalExtension); the rules that consume
 * it stay here, next to the exits they have to invert.
 */
export type SameVisualLine = (caretPos: number, boundaryPos: number) => boolean

/* Skipped for either direction: a stale proposal's from/to are the pre-collapse
 * spot held for the anti-flash window, so entering off them would open an
 * island against a position the settle pass is about to correct or drop, and a
 * non-widget proposal paints no card to enter. */
function entryCandidate(proposal: AIProposal): boolean {
  return !proposal.stale && widgetProposal(proposal)
}

// A vertical press only reaches a card on the neighbouring line, which is a
// line of the caret's OWN textblock: the card is a block child of its anchor's
// textblock, and its "after" landing spot is the first position past it.
function sameTextblock(state: EditorState, a: number, b: number): boolean {
  return state.doc.resolve(a).sameParent(state.doc.resolve(b))
}

/*
 * The island an ArrowLeft/ArrowRight press should move the caret into, or null
 * to let the host move the caret normally. `forward` is ArrowRight.
 *
 * Horizontal motion steps one position at a time, so the caret is on the
 * boundary exactly when the next press should enter — hence position equality.
 */
export function resolveHorizontalIslandEntry(
  state: EditorState,
  proposals: Iterable<AIProposal>,
  direction: IslandTraversalDirection
): IslandEntryTarget | null {
  const { selection } = state
  if (!selection.empty) {
    return null
  }
  const pos = selection.from
  for (const proposal of proposals) {
    if (!entryCandidate(proposal)) {
      continue
    }
    if (direction === 'forward') {
      if (proposal.to === pos) {
        return { id: proposal.id, edge: 'start' }
      }
      continue
    }
    if (islandExitPos(state.doc, proposal.to, 'after') === pos) {
      return { id: proposal.id, edge: 'end' }
    }
  }
  return null
}

/*
 * The island an ArrowUp/ArrowDown press should move the caret into, or null to
 * let the host move the caret normally. `forward` is ArrowDown.
 *
 * Down enters from anywhere on the card's own line — the anchor text the card
 * hangs under — and up from anywhere on the first line past it. Each direction
 * still requires the boundary to lie on the side of the caret the press moves
 * toward, so the exits stay exact inverses: an exit to `to` is never re-entered
 * by another ArrowUp, and an exit to `afterPos` is never re-entered by another
 * ArrowDown. Only the press that walks back the way it came returns.
 */
export function resolveVerticalIslandEntry(
  state: EditorState,
  proposals: Iterable<AIProposal>,
  direction: IslandTraversalDirection,
  sameVisualLine: SameVisualLine
): IslandEntryTarget | null {
  const { selection } = state
  if (!selection.empty) {
    return null
  }
  const pos = selection.from
  for (const proposal of proposals) {
    if (!entryCandidate(proposal)) {
      continue
    }
    if (direction === 'forward') {
      if (
        proposal.to >= pos &&
        sameTextblock(state, pos, proposal.to) &&
        sameVisualLine(pos, proposal.to)
      ) {
        return { id: proposal.id, edge: 'start' }
      }
      continue
    }
    const afterPos = islandExitPos(state.doc, proposal.to, 'after')
    if (
      afterPos !== null &&
      afterPos <= pos &&
      sameTextblock(state, pos, afterPos) &&
      sameVisualLine(pos, afterPos)
    ) {
      return { id: proposal.id, edge: 'end' }
    }
  }
  return null
}
